/**
 * Complete — session end card with a light check-in.
 * Ported (visually) from the standalone Calm The Body app (CompleteScreen.jsx).
 * The research-event logging from the source is intentionally omitted; this
 * version simply collects an optional check-in and calls onDone to leave.
 */
import { useState } from 'react';
import { CARE_TAGLINE } from '@/data/care';

type OutcomeId = 'calmer' | 'same' | 'not_helping';

const OUTCOMES: { id: OutcomeId; label: string }[] = [
  { id: 'calmer', label: 'Better' },
  { id: 'same', label: 'Same' },
  { id: 'not_helping', label: 'Worse' },
];

const RESPONSE: Record<OutcomeId, string> = {
  calmer: "That's good. Your nervous system responded. You can repeat this if you'd like more.",
  same: "That's okay. Regulation isn't always immediate. You can try a different approach or return to this later.",
  not_helping: "Breathing doesn't work for every moment. Other options may be more helpful right now.",
};

export function Complete({
  onDone,
  showCareNext = false,
  onOpenCare,
}: {
  onDone: () => void;
  showCareNext?: boolean;
  onOpenCare?: () => void;
}) {
  const [selected, setSelected] = useState<OutcomeId | null>(null);

  const checkedIn = selected !== null;

  return (
    <div className="cb-end-screen cb-fade-in">
      <div className="cb-end-content">
        <div className="cb-end-icon" aria-hidden="true">
          <div className="cb-end-orb" />
        </div>

        <h2 className="cb-end-title">Session complete.</h2>
        <p className="cb-end-subtitle">Notice what feels different, even slightly.</p>

        {!checkedIn ? (
          <div className="cb-end-checkin">
            <p className="cb-section-label">How are you feeling now?</p>
            <div className="cb-end-checkin-grid">
              {OUTCOMES.map((opt) => (
                <button key={opt.id} className="cb-end-checkin-btn" onClick={() => setSelected(opt.id)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="cb-end-response cb-fade-in">
            <p className="cb-end-response-text">{RESPONSE[selected]}</p>
          </div>
        )}
      </div>

      <div className="cb-end-actions">
        {checkedIn && showCareNext && onOpenCare && (
          <div className="cb-fade-in" style={{ marginBottom: 16 }}>
            <p className="cb-section-label">Do CARE next?</p>
            <p className="cb-end-response-text" style={{ marginBottom: 12 }}>
              {CARE_TAGLINE}
            </p>
            <button className="cb-btn-primary" onClick={onOpenCare}>
              Start CARE
            </button>
          </div>
        )}
        {checkedIn && (
          <button className={showCareNext && onOpenCare ? 'cb-btn-secondary' : 'cb-btn-primary'} onClick={onDone}>
            Done
          </button>
        )}
        <button className="cb-btn-ghost" onClick={onDone} style={{ marginTop: 16, alignSelf: 'center' }}>
          Return to start
        </button>
      </div>

      <div className="cb-end-note">
        <p>This tool supports regulation. It is not a substitute for therapy, medical care, or crisis support.</p>
      </div>
    </div>
  );
}
